// components/SensorTimeSeriesTable.js - Fixed to handle BME688 hierarchical data
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSensorMeasurements } from '../hooks/useSensorMeasurements';

// Helper function to format timestamp
function formatTimestamp(timestamp) {
  if (!timestamp) return 'N/A';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString();
  } catch (e) {
    return 'Invalid date';
  }
}

// Helper function to format values
function formatValue(value) {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }
  return String(value);
}

// Component to display BME688 measurements with profile tabs
function BME688TimeSeriesView({ measurements, onBack, onRefresh, loading }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedProfile, setSelectedProfile] = useState(0);

  // Extract all unique profiles from measurements
  const profiles = useMemo(() => {
    if (!Array.isArray(measurements) || measurements.length === 0) return [];

    // Get the first measurement to find available profiles
    const firstMeasurement = measurements[0];
    if (!firstMeasurement?.measurement_data?.bme688) return [];

    let bme688Data = firstMeasurement.measurement_data.bme688;

    // Convert to array if it's an object with numeric keys
    if (!Array.isArray(bme688Data) && typeof bme688Data === 'object') {
      const keys = Object.keys(bme688Data);
      const isArrayLike = keys.length > 0 && keys.every(k => !isNaN(parseInt(k)));
      if (isArrayLike) {
        bme688Data = Object.values(bme688Data);
      } else {
        return [];
      }
    }

    if (!Array.isArray(bme688Data)) return [];

    return bme688Data.map((profile, idx) => ({
      index: idx,
      name: profile.meas_profile || `Profile ${idx + 1}`
    }));
  }, [measurements]);

  // Extract data for selected profile
  const profileData = useMemo(() => {
    if (!Array.isArray(measurements) || measurements.length === 0) return [];

    return measurements
      .map(measurement => {
        let bme688Data = measurement?.measurement_data?.bme688;

        // Convert to array if needed
        if (!Array.isArray(bme688Data) && typeof bme688Data === 'object') {
          const keys = Object.keys(bme688Data);
          const isArrayLike = keys.length > 0 && keys.every(k => !isNaN(parseInt(k)));
          if (isArrayLike) {
            bme688Data = Object.values(bme688Data);
          }
        }

        if (!Array.isArray(bme688Data) || !bme688Data[selectedProfile]) return null;

        const profileMeasurement = bme688Data[selectedProfile];
        return {
          timestamp: measurement.timestamp,
          measurement_id: measurement.measurement_id,
          ...profileMeasurement
        };
      })
      .filter(Boolean);
  }, [measurements, selectedProfile]);

  // Get columns from the first data point
  const columns = useMemo(() => {
    if (profileData.length === 0) return [];
    const firstRow = profileData[0];
    return Object.keys(firstRow).filter(key =>
      key !== 'timestamp' && key !== 'measurement_id' && key !== 'meas_profile' && key !== 'meas_id'
    );
  }, [profileData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleProfileChange = (event, newValue) => {
    setSelectedProfile(newValue);
    setPage(0);
  };

  if (profiles.length === 0) {
    return (
      <Alert severity="warning">
        No BME688 profile data found in measurements
      </Alert>
    );
  }

  const paginatedData = profileData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedProfile} onChange={handleProfileChange}>
          {profiles.map((profile) => (
            <Tab
              key={profile.index}
              label={profile.name}
              value={profile.index}
            />
          ))}
        </Tabs>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600 }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, idx) => (
              <TableRow key={row.measurement_id || idx} hover>
                <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
                {columns.map((col) => (
                  <TableCell key={col}>{formatValue(row[col])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={profileData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
}

// Component to display regular sensor measurements
function RegularSensorTimeSeriesView({ measurements, sensorName, onBack, onRefresh, loading }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Extract sensor data from measurements
  const sensorData = useMemo(() => {
    if (!Array.isArray(measurements) || measurements.length === 0) return [];

    return measurements
      .map(measurement => {
        const data = measurement?.measurement_data?.[sensorName];
        if (!data || typeof data !== 'object') return null;

        return {
          timestamp: measurement.timestamp,
          measurement_id: measurement.measurement_id,
          ...data
        };
      })
      .filter(Boolean);
  }, [measurements, sensorName]);

  // Get columns from the first data point
  const columns = useMemo(() => {
    if (sensorData.length === 0) return [];
    const firstRow = sensorData[0];
    return Object.keys(firstRow).filter(key =>
      key !== 'timestamp' && key !== 'measurement_id'
    );
  }, [sensorData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (sensorData.length === 0) {
    return (
      <Alert severity="warning">
        No data found for sensor "{sensorName}"
      </Alert>
    );
  }

  const paginatedData = sensorData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600 }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, idx) => (
              <TableRow key={row.measurement_id || idx} hover>
                <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
                {columns.map((col) => (
                  <TableCell key={col}>{formatValue(row[col])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sensorData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
}

export default function SensorTimeSeriesTable({ boxId, sensorName, onBack }) {
  console.log('🔧 SensorTimeSeriesTable props:', { boxId, sensorName });

  const {
    measurements,
    loading,
    error,
    refetch,
    measurementsIsArray
  } = useSensorMeasurements(boxId, sensorName);

  console.log('📊 SensorTimeSeriesTable hook data:', {
    measurements,
    loading,
    error,
    measurementsType: typeof measurements,
    measurementsIsArray
  });

  const handleRefresh = () => {
    refetch();
  };

  // Determine if this is BME688 sensor
  const isBME688 = sensorName === 'bme688';

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'grey.50', p: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        bgcolor: 'white',
        p: 3,
        borderRadius: 2,
        boxShadow: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {sensorName} Time Series
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Box ID: {boxId}
              {isBME688 && <Chip label="Hierarchical Sensor" size="small" sx={{ ml: 1 }} />}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading data: {error.message || 'Unknown error'}
        </Alert>
      )}

      {/* No Data State */}
      {!loading && !error && (!measurements || (Array.isArray(measurements) && measurements.length === 0)) && (
        <Alert severity="info">
          No measurement data available for sensor "{sensorName}" in box "{boxId}".
        </Alert>
      )}

      {/* Data Display */}
      {!loading && !error && measurements && Array.isArray(measurements) && measurements.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Measurement Data
            </Typography>
            <Chip
              label={`${measurements.length} measurements`}
              color="primary"
              variant="outlined"
            />
          </Box>

          {isBME688 ? (
            <BME688TimeSeriesView
              measurements={measurements}
              onBack={onBack}
              onRefresh={handleRefresh}
              loading={loading}
            />
          ) : (
            <RegularSensorTimeSeriesView
              measurements={measurements}
              sensorName={sensorName}
              onBack={onBack}
              onRefresh={handleRefresh}
              loading={loading}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}